import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/DespesasCategorias_CarregaCategoriasInput.ts';
import '../contracts/DespesasCategorias_CreateInput.ts';
import '../contracts/DespesasCategorias_UpdateInput.ts';
import '../contracts/DespesasCategorias_DeleteInput.ts';

import '../contracts/DespesasCategorias_CarregaCategoriaResponse';
import '../contracts/DespesasCategorias_CarregaCategoriasResponse';
import '../contracts/DespesasCategorias_CreateResponse';
import '../contracts/DespesasCategorias_DeleteResponse';
import '../contracts/DespesasCategorias_UpdateResponse';
import type { DespesasCategorias_CreateInput } from '../contracts/DespesasCategorias_CreateInput';
import type { DespesasCategorias_DeleteInput } from '../contracts/DespesasCategorias_DeleteInput';
import type { DespesasCategorias_UpdateInput } from '../contracts/DespesasCategorias_UpdateInput';
/**
 * Composable for managing expense categories (DespesasCategorias)
 *
 * Provides methods for CRUD operations on expense categories
 */
export function useDespesasCategorias() {
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Fetch all expense categories
   * @returns {Promise<Array>} Array of category objects
   */
  const fetchCategorias = async () => {
    log('useDespesasCategorias', 'fetchCategorias', 'Fetching expense categories');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post('/index.php/config/despesas_categorias/carrega_categorias', null, { inputContract: 'DespesasCategorias_CarregaCategoriasInput', responseContract: 'DespesasCategorias_CarregaCategoriasResponse' });

      if (response.success) {
        log('useDespesasCategorias', 'fetchCategorias', 'Categories loaded successfully', response.dados);
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Erro ao carregar categorias');
      }
    } catch (err) {
      log('useDespesasCategorias', 'fetchCategorias', 'Error fetching categories', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch a single expense category by ID
   * @param {number} id - Category ID
   * @returns {Promise<Object>} Category object
   */
  const fetchCategoria = async (id: any) => {
    log('useDespesasCategorias', 'fetchCategoria', 'Fetching expense category', { id });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/config/despesas_categorias/carrega_categoria/${id}`, { responseContract: 'DespesasCategorias_CarregaCategoriaResponse' });

      if (response.success) {
        log('useDespesasCategorias', 'fetchCategoria', 'Category loaded successfully', response.dados);
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao carregar categoria');
      }
    } catch (err) {
      log('useDespesasCategorias', 'fetchCategoria', 'Error fetching category', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Create a new expense category
   * @param {Object} categoriaData - Category data
   * @param {string} categoriaData.despesa_categoria_titulo - Category title
   * @param {number} categoriaData.despesa_categoria_status - Status (0=Inactive, 1=Active)
   * @param {number} categoriaData.despesa_categoria_investimento - Is investment (0=No, 1=Yes)
   * @param {number} categoriaData.despesa_categoria_percentual_padrao - Default percentage
   * @returns {Promise<Object>} Created category with ID
   */
  const createCategoria = async (categoriaData: DespesasCategorias_CreateInput) => {
    log('useDespesasCategorias', 'createCategoria', 'Creating category', categoriaData);
    isLoading.value = true;
    error.value = null;

    try {
      validate('DespesasCategorias_CreateInput', categoriaData);
      const response = await httpService.post('/index.php/config/despesas_categorias/create', categoriaData, { responseContract: 'DespesasCategorias_CreateResponse' });

      if (response.success) {
        log('useDespesasCategorias', 'createCategoria', 'Category created successfully', response.dados);
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao criar categoria');
      }
    } catch (err) {
      log('useDespesasCategorias', 'createCategoria', 'Error creating category', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update an existing expense category
   * @param {number} id - Category ID
   * @param {Object} categoriaData - Category data to update
   * @returns {Promise<Object>} Response object
   */
  const updateCategoria = async (id: any, categoriaData: any) => {
    log('useDespesasCategorias', 'updateCategoria', 'Updating category', { id, categoriaData });
    isLoading.value = true;
    error.value = null;

    try {
      const payload: DespesasCategorias_UpdateInput = {
        despesa_categoria_id: id,
        ...categoriaData
      };

      validate('DespesasCategorias_UpdateInput', payload);
      const response = await httpService.post('/index.php/config/despesas_categorias/update', payload, { responseContract: 'DespesasCategorias_UpdateResponse' });

      if (response.success) {
        log('useDespesasCategorias', 'updateCategoria', 'Category updated successfully');
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao atualizar categoria');
      }
    } catch (err) {
      log('useDespesasCategorias', 'updateCategoria', 'Error updating category', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete an expense category
   * @param {number} id - Category ID
   * @returns {Promise<Object>} Response object
   */
  const deleteCategoria = async (id: any) => {
    log('useDespesasCategorias', 'deleteCategoria', 'Deleting category', { id });
    isLoading.value = true;
    error.value = null;

    try {
      const payload: DespesasCategorias_DeleteInput = { despesa_categoria_id: id };
      validate('DespesasCategorias_DeleteInput', payload);
      const response = await httpService.post('/index.php/config/despesas_categorias/delete', payload, { responseContract: 'DespesasCategorias_DeleteResponse' });

      if (response.success) {
        log('useDespesasCategorias', 'deleteCategoria', 'Category deleted successfully');
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao excluir categoria');
      }
    } catch (err) {
      log('useDespesasCategorias', 'deleteCategoria', 'Error deleting category', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    fetchCategorias,
    fetchCategoria,
    createCategoria,
    updateCategoria,
    deleteCategoria
  };
}
